import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import api from '../utils/api';
import { createSanitizedMarkup } from '../utils/sanitize';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await api.get(`/services/${slug}`);
        setService(response.data);
      } catch (error) {
        setError('Service not found');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-gray-400">{t.pages.loading}</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <div className="text-gray-400 mb-4">Service not found</div>
        <Link to="/hizmetler" className="text-blue-400 hover:text-blue-300">
          {t.pages.backToHome}
        </Link>
      </div>
    );
  }

  const title = language === 'tr' ? service.title_tr : service.title_en;
  const content = language === 'tr' ? service.content_tr : service.content_en;
  const IconComponent = Icons[service.icon] || Icons.Code;

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="service-detail-page">
      <div className="max-w-4xl mx-auto px-6">
        <ScrollReveal>
          {/* Back Link */}
          <Link 
            to="/hizmetler" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.services.allServices}
          </Link>

          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 
                          flex items-center justify-center shadow-lg shadow-blue-500/25">
              <IconComponent className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">{title}</h1>
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none
                       prose-headings:text-white prose-headings:font-bold
                       prose-p:text-gray-300 prose-li:text-gray-300
                       prose-strong:text-white prose-a:text-blue-400"
            dangerouslySetInnerHTML={createSanitizedMarkup(content)}
          />

          {/* CTA */}
          <div className="mt-16 p-8 rounded-2xl glass text-center">
            <h3 className="text-2xl font-bold mb-4">{t.cta.title1}</h3>
            <p className="text-gray-400 mb-6">{t.cta.subtitle}</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
              <Link
                to="/iletisim"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold 
                         inline-flex items-center gap-2 group"
              >
                {t.cta.btn1}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default ServiceDetail;
